import { NUTRIENT_KEYS, NUTRIENT_KEYS_ALL } from '../constants/constants';

/**
 * Normalizes the nutrient name for display
 * @param {String} name
 * @returns String - Normalized nutrient name
 */
export const getNutrientName = (name) => {
  if (name === 'Total Carbohydrates') {
    return 'Carbohydrates';
  }
  return name;
};

/**
 * Gets the description of a nutrient from the nutrientDvData
 *
 * @param {String} nutrient
 * @param {Object} nutrientDvData
 * @returns {String} nutrientDvData[nutrient].longDescription or empty string
 */
export const getNutrientDescription = (nutrient, nutrientDvData) => {
  if (!nutrientDvData || !nutrient) return '';
  const description = nutrientDvData[nutrient]?.longDescription;
  if (!description) return '';
  return description;
};

/**
 * Retrieves the dietary value (DV) of a nutrient from the nutrientDvData
 *
 * @param {String} nutrient
 * @param {Array} nutrientDvData
 * @returns
 */
export const getNutrientDietaryValue = (nutrient, nutrientDvData) => {
  return nutrientDvData?.[nutrient]?.dv || [];
};

/**
 * Formats nutrition information from data for display components
 *
 * @param {Object} data - The data object containing nutrition information
 * @param {string} type - Type of nutrients to format ('macronutrients', 'vitamins', etc.)
 * @param {Object} nutrientDvData - Optional dietary value data for nutrients
 * @returns {Array} Formatted nutrition data ready for display components
 */
export const formatNutrientData = (data, type = 'macronutrients', nutrientDvData = null) => {
  if (!data) return [];
  const nutritionInfo = data?.diet_id_nutrition_info;
  const idealNutritionInfo = data?.diet_ideal_nutrition_info;
  if (!nutritionInfo || !Array.isArray(nutritionInfo)) return [];
  return buildFormattedNutrients({
    nutrientArray: nutritionInfo,
    idealArray: idealNutritionInfo,
    type,
    nutrientDvData,
  });
};

/**
 * Convenience function to directly format step data
 * @param {Object} step - The step object from workflow
 * @param {string} type - Type of nutrients
 * @param {Object} nutrientDvData - Optional dietary value data
 * @returns {Array} Formatted nutrition data
 */
export const formatStepNutritionData = (step, type = 'macronutrients', nutrientDvData = null) => {
  if (!step) return null;

  return formatNutrientData(
    {
      diet_id_nutrition_info: step?.diet_id_nutrition_info || null,
      diet_ideal_nutrition_info: step?.diet_ideal_nutrition_info || null,
    },
    type,
    nutrientDvData,
  );
};

/**
 * Helper to format an array with nutrient data that does not have change data.
 *
 * @param {Array} data list of nutrients with no change data
 * @param {boolean} isIncrease
 * @returns Array of objects with label, change, and isIncrease properties or empty array
 */
export const getNutrientChangeData = (data, isIncrease) => {
  if (!data || !Array.isArray(data)) return [];

  // normalizing data for the nutrient profile box
  // missing difference data which is in the workflow_shares
  const change = data?.map((item) => ({
    label: item,
    change: { difference: 'unknown' },
    isIncrease: isIncrease,
  }));

  return change;
};

/**
 * Formats nutrition information for a single nutrient from an array
 *
 * @param {Array} nutrientArray - The array containing nutrient information
 * @param {string} type - Type of nutrients to format ('macronutrients', 'vitamins', etc.)
 * @param {Object} nutrientDvData - Optional dietary value data for nutrients
 * @returns {Array} Formatted nutrition data for the single nutrient
 */
export const formatSingleNutrientData = (nutrientArray, type = 'macronutrients', nutrientDvData = null) => {
  return buildFormattedNutrients({
    nutrientArray,
    idealArray: null,
    type,
    nutrientDvData,
  });
};

function buildFormattedNutrients({ nutrientArray, idealArray = null, type = 'macronutrients', nutrientDvData = null }) {
  if (!nutrientArray || !Array.isArray(nutrientArray)) return [];

  const keysToFind = type === 'all' ? NUTRIENT_KEYS_ALL : Object.values(NUTRIENT_KEYS[type] || {});
  if (type === 'macronutrients' && !keysToFind.includes(NUTRIENT_KEYS.macronutrients.ENERGY_KILOCALORIES)) {
    keysToFind.push(NUTRIENT_KEYS.macronutrients.ENERGY_KILOCALORIES);
  }

  const nutritionInfoMap = {};
  nutrientArray.forEach((info) => {
    if (info?.key) nutritionInfoMap[info.key] = info;
  });

  const idealNutritionInfoMap = {};
  if (idealArray && Array.isArray(idealArray)) {
    idealArray.forEach((info) => {
      if (info?.key) idealNutritionInfoMap[info.key] = info;
    });
  }

  const dailyCalories = nutritionInfoMap[NUTRIENT_KEYS.macronutrients.ENERGY_KILOCALORIES]?.value || 0;
  const formattedNutrients = [];

  keysToFind.forEach((key) => {
    if (!key) return;
    const info = nutritionInfoMap[key];
    const idealInfo = idealNutritionInfoMap[key];

    if (info) {
      const isEnergy = key === NUTRIENT_KEYS.macronutrients.ENERGY_KILOCALORIES;
      const isMacronutrient = Object.values(NUTRIENT_KEYS.macronutrients || {}).includes(key);
      const currentValue = Math.round(info?.value || 0);
      const goalValue = Math.round(idealInfo?.value || 0);

      let nutrient = {
        key,
        id: key
          .split('-')
          .map((word, index) => {
            if (index === 0) return word;
            return word.charAt(0).toUpperCase() + word.slice(1);
          })
          .join(''),
        name: getNutrientName(info.label?.trim() || ''),
        nutrientKey: key,
        unit: info.unit || (isEnergy ? 'kcal' : 'g'),
        current: currentValue.toString(),
        ...(nutrientDvData
          ? {
              dv: getNutrientDietaryValue(key, nutrientDvData),
              description: getNutrientDescription(key, nutrientDvData),
            }
          : {}),
        ...(isMacronutrient || isEnergy ? {} : { dailyCalories: Math.round(dailyCalories) }),
      };

      if (idealArray) {
        nutrient = { ...nutrient, goal: goalValue.toString() };
      }
      formattedNutrients.push(nutrient);
    }
  });

  return formattedNutrients;
}
